"use client"

import { useState, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import { useSelector } from "react-redux"
import axiosInstance from "../config/axios"
import TransactionHistory from "../components/shared/TransactionHistory"
import { useTour } from "../hooks/useTour"


// Chain data from backend config/coins.ts
const chains = [
  {
    id: 0,
    name: "Bitcoin",
    symbol: "BTC",
    image: "https://assets.coingecko.com/coins/images/1/large/bitcoin.png?1696501628",
    network: "bitcoin",
  },
  {
    id: 137,
    name: "Polygon",
    symbol: "POLYGON",
    image: "https://assets.coingecko.com/coins/images/4713/large/matic-token-icon.png?1696501625",
    network: "polygon",
  },
  {
    id: 1,
    name: "Ethereum",
    symbol: "ETH",
    image: "https://assets.coingecko.com/coins/images/279/large/ethereum.png?1696501625",
    network: "ethereum",
  },
  {
    id: 56,
    name: "Binance Smart Chain",
    symbol: "BSC",
    image: "https://assets.coingecko.com/coins/images/825/large/binance-coin-logo.png?1547034615",
    network: "bsc",
  },
  {
    id: 43114,
    name: "Avalanche",
    symbol: "AVAX_CCHAIN",
    image: "https://assets.coingecko.com/coins/images/12559/large/coin-round-red.png?1604021814",
    network: "avalanche",
  },
  {
    id: 1440,
    name: "Ripple",
    symbol: "XRPL",
    image: "https://assets.coingecko.com/coins/images/44/standard/xrp-symbol-white-128.png",
    network: "xrpl",
  },
  {
    id: 11,
    name: "Solana",
    symbol: "SOLANA",
    image: "https://assets.coingecko.com/coins/images/4128/large/solana.png?1696501625",
    network: "solana",
  },
]

export default function History() {
  const [selectedChain, setSelectedChain] = useState(137)
  const mode = useSelector((state) => state.mode.mode)
  const [walletType, setWalletType] = useState(mode === "private" ? "private" : "public") // 'public' or 'private'
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { startTour } = useTour();


  const walletTypes = [
    { value: "public", label: "Public" },
    { value: "private", label: "Private" },
  ]

  useEffect(() => {
    setWalletType(mode === "private" ? "private" : "public")
  }, [mode])

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalRecords: 0
  })

  const getExplorerUrl = (txId, chainId) => {
    const chain = chains.find(c => c.id === parseInt(chainId))
    if (!chain) return null

    const explorers = {
      0: `https://www.blockchain.com/btc/tx/${txId}`, // Bitcoin
      1: `https://etherscan.io/tx/${txId}`, // Ethereum
      137: `https://polygonscan.com/tx/${txId}`, // Polygon
      56: `https://bscscan.com/tx/${txId}`, // BSC
      43114: `https://snowtrace.io/tx/${txId}`, // Avalanche
      1440: `https://xrpscan.com/tx/${txId}`, // Ripple/XRPL
      11: `https://solscan.io/tx/${txId}`, // Solana
    }

    return explorers[chain.id] || null
  }

  const getPrivateExplorerUrl = (txId, chainId) => {
    const chain = chains.find(c => c.id === parseInt(chainId))
    if (!chain) return null

    const chainNames = {
      1: 'Ethereum',
      137: 'Polygon',
      56: 'BNB_Chain',
      43114: 'Avalanche',
    }

    const chainName = chainNames[chain.id]
    if (!chainName) return null

    return `https://ppoi.info/${chainName}/tx/${txId}`
  }

  // Fetch transactions when chain or wallet type changes or pagination changes
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true)
      setError(null)

      try {
        const endpoint = walletType === "public"
          ? `/wallet/public/history/chain/${selectedChain}`
          : `/wallet/private/history/chain/${selectedChain}`

        // Add query params for pagination if supported by backend
        const response = await axiosInstance.get(endpoint, {
          params: { page: pagination.page, limit: pagination.limit }
        })

        if (response.data.success && response.data.data) {
          const txData = response.data.data.transactions;
          setTransactions(txData || [])

          // Update pagination if data is available
          if (response.data.data.pagination) {
            setPagination(response.data.data.pagination)
          } else {
            // Fallback for non-paginated API
            setPagination(prev => ({
              ...prev,
              totalRecords: txData?.length || 0,
              totalPages: Math.ceil((txData?.length || 0) / prev.limit)
            }))
          }
        }
      } catch (err) {
        console.error('Error fetching transactions:', err)
        setError('Failed to fetch transaction history')
        setTransactions([])
      } finally {
        setLoading(false)
      }
    }

    fetchTransactions()
  }, [selectedChain, walletType, pagination.page])

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }

  const handleTransactionClick = (transaction) => {
    const txHash = transaction.hash || transaction.txHash || transaction.transactionHash || transaction.txId;
    if (!txHash) return

    let explorerUrl = null

    if (walletType === 'public') {
      explorerUrl = getExplorerUrl(txHash, selectedChain)
    } else if (walletType === 'private') {
      explorerUrl = getPrivateExplorerUrl(txHash, selectedChain)
    }

    if (explorerUrl) {
      window.open(explorerUrl, '_blank', 'noopener,noreferrer')
    }
  }

  useEffect(() => {
    document.title = "History - KAXAA Wallet";
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      startTour(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [startTour]);


  return (
    <div className="wallet-dashboard">
      <div className="">
        {/* Header */}

        <div className="flex md:flex-row md:items-center flex-col gap-6 justify-between mb-8">
          <div className="" id="tour-history-header">
            <h1 className="font-semibold text-18 mb-2">Transaction History</h1>
            <p className="text-muted-foreground text-gray-600 m-0">
              View your wallet transactions by chain and wallet type
            </p>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 gap-4 lg:w-3/12">
            {/* Chain Filter */}
            <div className="flex flex-col gap-0" id="tour-chain-selector">
              <label className="font-semibold">Select Chain</label>
              <div className="relative">
                <select
                  value={selectedChain}
                  onChange={(e) => {
                    setSelectedChain(Number(e.target.value));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="form-control border-gray-300"
                >
                  {chains.map((chain) => (
                    <option key={chain.id} value={chain.id}>
                      {chain.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger text-center py-4 mb-8" role="alert">
            {error}
          </div>
        )}

        <div id="tour-history-table">
          <div className="mb-8">
            <TransactionHistory
              transactions={transactions}
              loading={loading}
              pagination={pagination}
              onPageChange={handlePageChange}
              onTransactionClick={handleTransactionClick}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
